// Phase 15 Plan 06 Task 3 — Friend-code invite modal.
//
// Larger modal (max-height 80vh + body scroll) hosting the DEC-024
// host-config form (5 Seg pickers + friend-code text input). On
// submit → POST /api/friends/match (sendFriendMatchInvite) → toast
// `social.friends.invite_sent` + close. 4xx → inline error
// `social.error.friend_code_invalid` (oracle-safe per Phase-7).
//
// Default host-config values match DEC-024 casual defaults: minWordLength 3,
// turnClockMs 86_400_000 (1 day), lengthCap 30, powerupMode '4-of-8' (with
// the default 4-power-up loadout from FriendMatchConfig).
//
// gap-MUI-03 (milestone-audit fix): when powerupMode === '4-of-8',
// surface a "Choose 4 power-ups" button that opens LoadoutPickerModal
// (mode='friend'). The picked tuple flows into FriendMatchConfig.
// powerupLoadout on Send invite. If the user doesn't open the picker
// the config falls back to the DEC-024 casual default. The picker
// persists to localStorage via setLoadoutForMode('friend', ...) so
// next-time-friend-match re-opens with the same selection.

import { type JSX, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import type { PowerUpName } from '../../game/PlayerViewSchema'
import { ApiError } from '../../net/client'
import {
  type FriendMatchConfig,
  sendFriendMatchInvite,
} from '../../net/friends'
import { Btn } from '../components/Btn'
import { Seg, type SegOption } from '../components/Seg'
import { Sheet } from '../components/Sheet'
import { LoadoutPickerModal } from '../match/LoadoutPickerModal'

// All 8 power-ups — used as `availablePowerups` for the picker on the
// friend-match host-config surface. DEC-024 caps at 4-of-8 server-side;
// all 8 are eligible to be picked.
const ALL_POWERUPS: readonly PowerUpName[] = [
  'scriveners_quill',
  'tongue_tie',
  'knights_vow',
  'whisperers_promise',
  'lexical_mulligan',
  'the_vanishing',
  'castle_keep',
  'anagram_sigil',
]

export interface FriendCodeModalProps {
  readonly open: boolean
  readonly onClose: () => void
}

type MinWord = 3 | 4
type TurnClock = 10_800_000 | 86_400_000 | 259_200_000
type LengthCap = 0 | 20 | 30 | 40
type PowerupMode = 'off' | '4-of-8' | 'all-8'
type Dictionary = 'en' | 'sv'

const MIN_WORD_OPTIONS: ReadonlyArray<SegOption<'3' | '4'>> = [
  {
    value: '3',
    label: (
      <FormattedMessage
        id="social.lobby.friend_code_modal.min_word.3"
        defaultMessage="3 letters"
      />
    ),
    ariaLabel: '3 letters',
  },
  {
    value: '4',
    label: (
      <FormattedMessage
        id="social.lobby.friend_code_modal.min_word.4"
        defaultMessage="4 letters"
      />
    ),
    ariaLabel: '4 letters',
  },
]

const TURN_CLOCK_OPTIONS: ReadonlyArray<SegOption<'3h' | '1d' | '3d'>> = [
  {
    value: '3h',
    label: (
      <FormattedMessage
        id="social.lobby.friend_code_modal.turn_clock.3h"
        defaultMessage="3 hours"
      />
    ),
    ariaLabel: '3 hours',
  },
  {
    value: '1d',
    label: (
      <FormattedMessage
        id="social.lobby.friend_code_modal.turn_clock.1d"
        defaultMessage="1 day"
      />
    ),
    ariaLabel: '1 day',
  },
  {
    value: '3d',
    label: (
      <FormattedMessage
        id="social.lobby.friend_code_modal.turn_clock.3d"
        defaultMessage="3 days"
      />
    ),
    ariaLabel: '3 days',
  },
]

const LENGTH_CAP_OPTIONS: ReadonlyArray<
  SegOption<'20' | '30' | '40' | 'unlimited'>
> = [
  {
    value: '20',
    label: (
      <FormattedMessage
        id="social.lobby.friend_code_modal.length_cap.20"
        defaultMessage="20 turns"
      />
    ),
    ariaLabel: '20 turns',
  },
  {
    value: '30',
    label: (
      <FormattedMessage
        id="social.lobby.friend_code_modal.length_cap.30"
        defaultMessage="30 turns"
      />
    ),
    ariaLabel: '30 turns',
  },
  {
    value: '40',
    label: (
      <FormattedMessage
        id="social.lobby.friend_code_modal.length_cap.40"
        defaultMessage="40 turns"
      />
    ),
    ariaLabel: '40 turns',
  },
  {
    value: 'unlimited',
    label: (
      <FormattedMessage
        id="social.lobby.friend_code_modal.length_cap.unlimited"
        defaultMessage="No cap"
      />
    ),
    ariaLabel: 'No cap',
  },
]

const POWERUP_MODE_OPTIONS: ReadonlyArray<SegOption<PowerupMode>> = [
  {
    value: 'off',
    label: (
      <FormattedMessage
        id="social.lobby.friend_code_modal.powerup.off"
        defaultMessage="No power-ups"
      />
    ),
    ariaLabel: 'No power-ups',
  },
  {
    value: '4-of-8',
    label: (
      <FormattedMessage
        id="social.lobby.friend_code_modal.powerup.four_of_eight"
        defaultMessage="4 of 8"
      />
    ),
    ariaLabel: '4 of 8',
  },
  {
    value: 'all-8',
    label: (
      <FormattedMessage
        id="social.lobby.friend_code_modal.powerup.all_eight"
        defaultMessage="All 8"
      />
    ),
    ariaLabel: 'All 8',
  },
]

const DICTIONARY_OPTIONS: ReadonlyArray<SegOption<Dictionary>> = [
  {
    value: 'en',
    label: (
      <FormattedMessage
        id="social.lobby.friend_code_modal.dictionary.en"
        defaultMessage="English"
      />
    ),
    ariaLabel: 'English',
  },
  {
    value: 'sv',
    label: (
      <FormattedMessage
        id="social.lobby.friend_code_modal.dictionary.sv"
        defaultMessage="Swedish"
      />
    ),
    ariaLabel: 'Swedish',
  },
]

// Casual DEC-024 default loadout used when powerupMode === '4-of-8'.
const DEFAULT_FOUR_OF_EIGHT: ReadonlyArray<
  'scriveners_quill' | 'tongue_tie' | 'knights_vow' | 'whisperers_promise'
> = ['scriveners_quill', 'tongue_tie', 'knights_vow', 'whisperers_promise']

const FRIEND_CODE_RE = /^[A-HJ-NP-Z2-9]{3}-[A-HJ-NP-Z2-9]{4}$/

function turnClockToMs(value: '3h' | '1d' | '3d'): TurnClock {
  if (value === '3h') return 10_800_000
  if (value === '3d') return 259_200_000
  return 86_400_000
}

function lengthCapToValue(value: '20' | '30' | '40' | 'unlimited'): LengthCap {
  if (value === '20') return 20
  if (value === '30') return 30
  if (value === '40') return 40
  return 0
}

export function FriendCodeModal({
  open,
  onClose,
}: FriendCodeModalProps): JSX.Element | null {
  const intl = useIntl()
  const [friendCode, setFriendCode] = useState<string>('')
  const [minWord, setMinWord] = useState<'3' | '4'>('3')
  const [turnClock, setTurnClock] = useState<'3h' | '1d' | '3d'>('1d')
  const [lengthCap, setLengthCap] = useState<'20' | '30' | '40' | 'unlimited'>(
    '30',
  )
  const [powerupMode, setPowerupMode] = useState<PowerupMode>('4-of-8')
  const [dictionary, setDictionary] = useState<Dictionary>('en')
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  // gap-MUI-03: user-picked 4-of-8 loadout (null until LoadoutPickerModal
  // emits onLockIn). When null the config falls back to DEFAULT_FOUR_OF_EIGHT
  // — DEC-024 casual default — so the modal remains submittable even if the
  // user doesn't open the picker.
  const [pickedLoadout, setPickedLoadout] = useState<
    readonly PowerUpName[] | null
  >(null)
  const [loadoutPickerOpen, setLoadoutPickerOpen] = useState<boolean>(false)

  if (!open) return null

  const trimmedCode = friendCode.trim().toUpperCase()
  const codeLooksValid = FRIEND_CODE_RE.test(trimmedCode)

  const handleSubmit = async (): Promise<void> => {
    if (submitting) return
    setError(null)
    if (!codeLooksValid) {
      setError(
        intl.formatMessage({
          id: 'social.error.friend_code_invalid',
          defaultMessage: "That code didn't match anyone.",
        }),
      )
      return
    }
    setSubmitting(true)
    try {
      const minWordLength: MinWord = minWord === '3' ? 3 : 4
      // gap-MUI-03: use the user-picked loadout when present; fall back to
      // the DEC-024 casual default. powerupLoadout is REQUIRED iff
      // powerupMode === '4-of-8' per FriendMatchConfig (openapi.yaml
      // lines 1392-1401); MUST be absent for 'off' / 'all-8'.
      const effectiveLoadout: ReadonlyArray<PowerUpName> =
        pickedLoadout !== null ? pickedLoadout : [...DEFAULT_FOUR_OF_EIGHT]
      const cfg: FriendMatchConfig =
        powerupMode === '4-of-8'
          ? {
              language: dictionary,
              minWordLength,
              turnClockMs: turnClockToMs(turnClock),
              lengthCap: lengthCapToValue(lengthCap),
              powerupMode,
              powerupLoadout: [...effectiveLoadout],
            }
          : {
              language: dictionary,
              minWordLength,
              turnClockMs: turnClockToMs(turnClock),
              lengthCap: lengthCapToValue(lengthCap),
              powerupMode,
            }
      await sendFriendMatchInvite(trimmedCode, cfg)
      // Success toast + close.
      window.dispatchEvent(
        new CustomEvent('runegambit:toast', {
          detail: { id: 'social.friends.invite_sent' },
        }),
      )
      onClose()
    } catch (e) {
      // Oracle-safe — 404 collapses all friendship-graph failures into a
      // single byte-equal error per Phase-7 hardening. We don't surface
      // distinguishing details.
      if (e instanceof ApiError && (e.status === 404 || e.status === 400)) {
        setError(
          intl.formatMessage({
            id: 'social.error.friend_code_invalid',
            defaultMessage: "That code didn't match anyone.",
          }),
        )
      } else if (e instanceof ApiError && e.status === 409) {
        setError(
          intl.formatMessage({
            id: 'social.error.cap_reached',
            defaultMessage: "You've reached your match cap.",
          }),
        )
      } else {
        setError(
          intl.formatMessage({
            id: 'social.error.friend_code_invalid',
            defaultMessage: "That code didn't match anyone.",
          }),
        )
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      {/* Bottom-sheet flavor (UI-SPEC §5): scrim + grab handle + focus trap +
          scrim-tap/Escape dismissal live in <Sheet>/ModalScrim.
          panelStyle provides the flex-column + 80vh max-height for the
          scrollable body. dismissOnScrimTap guards against accidental close
          during submit. */}
      <Sheet
        onClose={onClose}
        testId="friend-code-modal"
        labelledBy="friend-code-modal-title"
        dismissOnScrimTap={!submitting}
        panelStyle={{
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '80vh',
        }}
      >
        <div
          style={{
            height: '48px',
            backgroundColor: 'var(--color-surface-deep)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            borderBottom: '3px solid var(--color-surface-stroke)',
            flexShrink: 0,
          }}
        >
          <h2
            id="friend-code-modal-title"
            style={{
              margin: 0,
              fontFamily: 'Fredoka, sans-serif',
              fontWeight: 700,
              fontSize: 'var(--font-size-18)',
              color: 'var(--color-text-primary)',
            }}
          >
            <FormattedMessage
              id="social.lobby.friend_code_modal.title"
              defaultMessage="Challenge a friend"
            />
          </h2>
        </div>
        <div
          style={{
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '14px',
            overflowY: 'auto',
            color: 'var(--color-text-primary)',
            fontFamily: 'Manrope, system-ui, sans-serif',
          }}
        >
          <label
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
              fontWeight: 500,
              fontSize: 'var(--font-size-14)',
            }}
          >
            <FormattedMessage
              id="social.lobby.friend_code_modal.code_label"
              defaultMessage="Friend code"
            />
            <input
              data-testid="friend-code-input"
              type="text"
              value={friendCode}
              onChange={(e) => setFriendCode(e.target.value)}
              autoCapitalize="characters"
              spellCheck={false}
              placeholder={intl.formatMessage({
                id: 'social.lobby.friend_code_modal.code_placeholder',
                defaultMessage: 'e.g. RG7-K2M9',
              })}
              style={{
                height: '44px',
                padding: '0 var(--space-3)',
                fontFamily: 'Fredoka, sans-serif',
                fontWeight: 700,
                fontSize: 'var(--font-size-16)',
                textTransform: 'uppercase',
                backgroundColor: 'var(--color-surface-deep)',
                color: 'var(--color-text-primary)',
                border: '2px solid var(--color-surface-stroke)',
                borderRadius: 'var(--radius-lg)',
              }}
            />
          </label>
          <FieldRow
            labelId="social.lobby.friend_code_modal.min_word_label"
            labelDefault="Word minimum"
          >
            <Seg<'3' | '4'>
              options={MIN_WORD_OPTIONS}
              value={minWord}
              onChange={setMinWord}
              ariaLabel={intl.formatMessage({
                id: 'social.lobby.friend_code_modal.min_word_label',
                defaultMessage: 'Word minimum',
              })}
              testId="friend-min-word-pills"
            />
          </FieldRow>
          <FieldRow
            labelId="social.lobby.friend_code_modal.turn_clock_label"
            labelDefault="Turn clock"
          >
            <Seg<'3h' | '1d' | '3d'>
              options={TURN_CLOCK_OPTIONS}
              value={turnClock}
              onChange={setTurnClock}
              ariaLabel={intl.formatMessage({
                id: 'social.lobby.friend_code_modal.turn_clock_label',
                defaultMessage: 'Turn clock',
              })}
              testId="friend-turn-clock-pills"
            />
          </FieldRow>
          <FieldRow
            labelId="social.lobby.friend_code_modal.length_cap_label"
            labelDefault="Match length cap"
          >
            <Seg<'20' | '30' | '40' | 'unlimited'>
              options={LENGTH_CAP_OPTIONS}
              value={lengthCap}
              onChange={setLengthCap}
              ariaLabel={intl.formatMessage({
                id: 'social.lobby.friend_code_modal.length_cap_label',
                defaultMessage: 'Match length cap',
              })}
              testId="friend-length-cap-pills"
            />
          </FieldRow>
          <FieldRow
            labelId="social.lobby.friend_code_modal.powerup_label"
            labelDefault="Power-ups"
          >
            <Seg<PowerupMode>
              options={POWERUP_MODE_OPTIONS}
              value={powerupMode}
              onChange={setPowerupMode}
              ariaLabel={intl.formatMessage({
                id: 'social.lobby.friend_code_modal.powerup_label',
                defaultMessage: 'Power-ups',
              })}
              testId="friend-powerup-pills"
            />
          </FieldRow>
          {/* gap-MUI-03: 4-of-8 loadout picker affordance. Visible only
              when the host has selected 4-of-8 power-ups (the only
              powerupMode that carries a powerupLoadout per the
              FriendMatchConfig schema). */}
          {powerupMode === '4-of-8' && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
              }}
            >
              <button
                type="button"
                data-testid="friend-choose-loadout-cta"
                onClick={() => setLoadoutPickerOpen(true)}
                style={{
                  height: '44px',
                  padding: '0 var(--space-3)',
                  backgroundColor: 'var(--color-surface-deep)',
                  color: 'var(--color-text-primary)',
                  border: '2px solid var(--color-surface-stroke)',
                  borderRadius: 'var(--radius-lg)',
                  fontFamily: 'Fredoka, sans-serif',
                  fontWeight: 700,
                  fontSize: 'var(--font-size-14)',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <FormattedMessage
                  id="social.lobby.friend_code_modal.choose_loadout_cta"
                  defaultMessage="Choose 4 power-ups"
                />
              </button>
              {pickedLoadout !== null && (
                <p
                  data-testid="friend-loadout-summary"
                  style={{
                    margin: 0,
                    fontFamily: 'Manrope, system-ui, sans-serif',
                    fontWeight: 500,
                    fontSize: 'var(--font-size-13)',
                    color: 'var(--color-text-primary)',
                    opacity: 0.85,
                  }}
                >
                  {pickedLoadout
                    .map((p) =>
                      intl.formatMessage({ id: `match.powerup.name.${p}` }),
                    )
                    .join(', ')}
                </p>
              )}
            </div>
          )}
          <FieldRow
            labelId="social.lobby.friend_code_modal.dictionary_label"
            labelDefault="Dictionary"
          >
            <Seg<Dictionary>
              options={DICTIONARY_OPTIONS}
              value={dictionary}
              onChange={setDictionary}
              ariaLabel={intl.formatMessage({
                id: 'social.lobby.friend_code_modal.dictionary_label',
                defaultMessage: 'Dictionary',
              })}
              testId="friend-dictionary-pills"
            />
          </FieldRow>
          {error !== null && (
            <p
              role="alert"
              data-testid="friend-code-error"
              style={{
                margin: 0,
                color: 'var(--color-cta-destructive)',
                fontSize: 'var(--font-size-13)',
              }}
            >
              {error}
            </p>
          )}
          <Btn
            kind="primary"
            size="lg"
            block
            testId="friend-code-submit"
            disabled={submitting}
            onClick={() => {
              void handleSubmit()
            }}
          >
            <FormattedMessage
              id="social.lobby.friend_code_modal.send_invite_cta"
              defaultMessage="Send invite"
            />
          </Btn>
        </div>
      </Sheet>
      {/* gap-MUI-03: nested LoadoutPickerModal for the 4-of-8 host-config
          surface. The picker persists the picked tuple via
          setLoadoutForMode('friend', ...) so next-time-friend-match re-
          opens with the same selection. The picked tuple flows into
          FriendMatchConfig.powerupLoadout on Send invite (handleSubmit). */}
      <LoadoutPickerModal
        open={loadoutPickerOpen}
        mode="friend"
        availablePowerups={ALL_POWERUPS}
        onLockIn={(picked) => {
          setPickedLoadout(picked)
          setLoadoutPickerOpen(false)
        }}
        onCancel={() => setLoadoutPickerOpen(false)}
      />
    </>
  )
}

function FieldRow({
  labelId,
  labelDefault,
  children,
}: {
  labelId: string
  labelDefault: string
  children: JSX.Element
}): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <span
        style={{
          fontFamily: 'Manrope, system-ui, sans-serif',
          fontWeight: 500,
          fontSize: 'var(--font-size-13)',
          color: 'var(--color-text-primary)',
        }}
      >
        <FormattedMessage id={labelId} defaultMessage={labelDefault} />
      </span>
      {children}
    </div>
  )
}
